import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  useTimeEntries,
  useMergedCategories,
  useCreateTimeEntry,
  useDeleteTimeEntry,
  useCreateTimeCategory,
  DEFAULT_CATEGORIES,
} from '../helpers/useTimeEntries';
import { useTodos } from '../helpers/useTodosApi';
import styles from './TimeTrackerPanel.module.css';

/* ─── Helpers ─── */
const fmt = (s: number) => {
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
};

const fmtDur = (s: number) => {
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${h}h ${m}m`;
};

const fmtRange = (a: string, b: string) => {
  const f = (t: string) =>
    new Date(t).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  return `${f(a)} — ${f(b)}`;
};

const RADIUS = 80;
const CIRC = 2 * Math.PI * RADIUS;
const TICKS = 60;

const PROJECT_COLORS = [
  '#4a7cdb',
  '#c76b8a',
  '#c9a84c',
  '#3a9a8a',
  '#6aab6a',
  '#9a5ad4',
  '#d4654a',
  '#4ad4b0',
  '#db7c4a',
  '#7c4adb',
];

/* ─── SVG Ticks ─── */
function Ticks() {
  return (
    <>
      {Array.from({ length: TICKS }, (_, i) => {
        const angle = (i * 6 - 90) * (Math.PI / 180);
        const major = i % 5 === 0;
        const cx = 100,
          cy = 100;
        const r1 = major ? 72 : 74;
        const r2 = major ? 65 : 70;
        return (
          <line
            key={i}
            x1={cx + r1 * Math.cos(angle)}
            y1={cy + r1 * Math.sin(angle)}
            x2={cx + r2 * Math.cos(angle)}
            y2={cy + r2 * Math.sin(angle)}
            stroke={major ? 'rgba(201,168,76,0.3)' : 'rgba(201,168,76,0.1)'}
            strokeWidth={major ? 1.2 : 0.5}
          />
        );
      })}
    </>
  );
}

/* ─── Component ─── */
interface TimeTrackerPanelProps {
  sessionId: string;
}

export function TimeTrackerPanel({ sessionId }: TimeTrackerPanelProps) {
  const { data: entriesData } = useTimeEntries(sessionId);
  const { data: todosData } = useTodos(sessionId);
  const categories = useMergedCategories();
  const createEntry = useCreateTimeEntry();
  const deleteEntry = useDeleteTimeEntry();
  const createCategory = useCreateTimeCategory();

  const entries = entriesData?.entries || [];
  const todos = todosData?.todos || [];

  /* Timer state */
  const [timerState, setTimerState] = useState<'idle' | 'running' | 'paused'>('idle');
  const [elapsed, setElapsed] = useState(0);
  const [sessionStart, setSessionStart] = useState<number | null>(null);
  const timerStartRef = useRef<number | null>(null);
  const pausedRef = useRef(0);
  const intervalRef = useRef<number | null>(null);

  /* Form state */
  const [activeCat, setActiveCat] = useState(categories[0]?.name || 'Development');
  const [activeCatColor, setActiveCatColor] = useState(categories[0]?.color || '#4a7cdb');
  const [note, setNote] = useState('');
  const [todoId, setTodoId] = useState<string | null>(null);
  const [showAddCat, setShowAddCat] = useState(false);
  const [newCatName, setNewCatName] = useState('');

  /* Timer tick */
  useEffect(() => {
    if (timerState === 'running' && timerStartRef.current) {
      intervalRef.current = window.setInterval(() => {
        setElapsed(pausedRef.current + Math.floor((Date.now() - timerStartRef.current!) / 1000));
      }, 200);
    } else if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [timerState]);

  /* Keyboard shortcuts */
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (
        (e.target as HTMLElement).tagName === 'INPUT' ||
        (e.target as HTMLElement).tagName === 'SELECT'
      )
        return;
      if (e.code === 'Space') {
        e.preventDefault();
        if (timerState === 'idle') startTimer();
        else if (timerState === 'running') pauseTimer();
        else startTimer();
      }
      if (e.code === 'Escape' && timerState !== 'idle') stopTimer();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  });

  const startTimer = () => {
    const now = Date.now();
    if (timerState === 'idle') {
      setSessionStart(now);
      pausedRef.current = 0;
    }
    timerStartRef.current = now;
    setTimerState('running');
  };

  const pauseTimer = () => {
    pausedRef.current = elapsed;
    timerStartRef.current = null;
    setTimerState('paused');
  };

  const stopTimer = () => {
    if (elapsed > 0 && sessionStart) {
      createEntry.mutate({
        sessionId,
        category: activeCat,
        categoryColor: activeCatColor,
        todoId: todoId || undefined,
        note: note.trim(),
        startTime: new Date(sessionStart).toISOString(),
        endTime: new Date().toISOString(),
        duration: elapsed,
      });
    }
    setTimerState('idle');
    setElapsed(0);
    pausedRef.current = 0;
    timerStartRef.current = null;
    setSessionStart(null);
    setNote('');
    setTodoId(null);
  };

  const selectCategory = (name: string, color: string) => {
    setActiveCat(name);
    setActiveCatColor(color);
  };

  const addCategory = () => {
    if (!newCatName.trim()) return;
    const color = PROJECT_COLORS[categories.length % PROJECT_COLORS.length];
    createCategory.mutate({ name: newCatName.trim(), color });
    setNewCatName('');
    setShowAddCat(false);
  };

  /* Stats */
  const today = new Date().toDateString();
  const todayEntries = entries.filter((e) => new Date(e.startTime).toDateString() === today);
  const todayTotal = todayEntries.reduce((s, e) => s + e.duration, 0);

  /* Ring */
  const progress = Math.min(elapsed / 3600, 1);
  const dashOffset = CIRC * (1 - progress);

  /* Breakdown */
  const breakdown = useMemo(() => {
    const map = new Map<string, { color: string; total: number }>();
    for (const e of todayEntries) {
      const existing = map.get(e.category);
      if (existing) existing.total += e.duration;
      else map.set(e.category, { color: e.categoryColor, total: e.duration });
    }
    return Array.from(map.entries())
      .map(([name, { color, total }]) => ({ name, color, total }))
      .sort((a, b) => b.total - a.total);
  }, [todayEntries]);

  return (
    <div className={styles.panel}>
      {/* Stats bar */}
      <div className={styles.statsBar}>
        <div className={styles.stat}>
          <span className={styles.statLabel}>Today</span>
          <span className={styles.statVal}>
            {fmtDur(todayTotal + (timerState !== 'idle' ? elapsed : 0))}
          </span>
        </div>
        <div className={styles.stat}>
          <span className={styles.statLabel}>Entries</span>
          <span className={styles.statVal}>{todayEntries.length}</span>
        </div>
        <Link to="/timekeeper" className={styles.dashLink} title="Open full dashboard">
          <svg
            width="14"
            height="14"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          >
            <path
              d="M6 2H2v4M14 2h-4M14 10v4h-4M2 10v4h4M2 6V2h4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </Link>
      </div>

      {/* Timer ring */}
      <div className={styles.timerWrap}>
        <svg className={styles.timerSvg} viewBox="0 0 200 200">
          <Ticks />
          <circle
            cx="100"
            cy="100"
            r={RADIUS}
            fill="none"
            stroke="rgba(201,168,76,0.06)"
            strokeWidth="4"
          />
          <circle
            className={styles.timerArc}
            cx="100"
            cy="100"
            r={RADIUS}
            fill="none"
            stroke={activeCatColor}
            strokeWidth="4"
            strokeLinecap="round"
            strokeDasharray={CIRC}
            strokeDashoffset={dashOffset}
            transform="rotate(-90 100 100)"
            style={{ filter: `drop-shadow(0 0 6px ${activeCatColor}40)` }}
          />
        </svg>
        <div className={styles.timerFace}>
          <div className={`${styles.digits} ${timerState === 'paused' ? styles.blink : ''}`}>
            {fmt(elapsed)}
          </div>
          <div className={styles.catLabel} style={{ color: activeCatColor }}>
            {activeCat}
          </div>
        </div>
      </div>

      {/* Note + Todo */}
      <input
        className={styles.noteInput}
        placeholder="What are you working on?"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && timerState === 'idle') startTimer();
        }}
      />

      {todos.length > 0 && (
        <select
          className={styles.todoSelect}
          value={todoId || ''}
          onChange={(e) => setTodoId(e.target.value || null)}
        >
          <option value="">Link to todo (optional)</option>
          {todos.map((t: any) => (
            <option key={t.id} value={t.id}>
              {t.title}
            </option>
          ))}
        </select>
      )}

      {/* Controls */}
      <div className={styles.controls}>
        {timerState === 'idle' && (
          <button className={`${styles.btn} ${styles.btnStart}`} onClick={startTimer}>
            <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14">
              <polygon points="5,3 17,10 5,17" />
            </svg>
            Start
          </button>
        )}
        {timerState === 'running' && (
          <>
            <button className={`${styles.btn} ${styles.btnPause}`} onClick={pauseTimer}>
              <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14">
                <rect x="4" y="3" width="4" height="14" rx="1" />
                <rect x="12" y="3" width="4" height="14" rx="1" />
              </svg>
              Pause
            </button>
            <button className={`${styles.btn} ${styles.btnStop}`} onClick={stopTimer}>
              <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14">
                <rect x="4" y="4" width="12" height="12" rx="2" />
              </svg>
              Stop
            </button>
          </>
        )}
        {timerState === 'paused' && (
          <>
            <button className={`${styles.btn} ${styles.btnStart}`} onClick={startTimer}>
              <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14">
                <polygon points="5,3 17,10 5,17" />
              </svg>
              Resume
            </button>
            <button className={`${styles.btn} ${styles.btnStop}`} onClick={stopTimer}>
              <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14">
                <rect x="4" y="4" width="12" height="12" rx="2" />
              </svg>
              Stop
            </button>
          </>
        )}
      </div>

      {/* Category chips */}
      <div className={styles.chipBar}>
        {categories.map((c) => (
          <button
            key={c.id || c.name}
            className={`${styles.chip} ${activeCat === c.name ? styles.chipActive : ''}`}
            onClick={() => selectCategory(c.name, c.color)}
            style={{
              borderColor: activeCat === c.name ? c.color : 'transparent',
              background: activeCat === c.name ? `${c.color}18` : undefined,
            }}
          >
            <span className={styles.chipDot} style={{ background: c.color }} />
            {c.name}
          </button>
        ))}
        {!showAddCat ? (
          <button
            className={`${styles.chip} ${styles.chipAdd}`}
            onClick={() => setShowAddCat(true)}
          >
            + Add
          </button>
        ) : (
          <form
            className={styles.addForm}
            onSubmit={(e) => {
              e.preventDefault();
              addCategory();
            }}
          >
            <input
              autoFocus
              className={styles.addInput}
              placeholder="Name"
              value={newCatName}
              onChange={(e) => setNewCatName(e.target.value)}
              onBlur={() => {
                if (!newCatName.trim()) setShowAddCat(false);
              }}
            />
            <button type="submit" className={styles.addBtn}>
              +
            </button>
          </form>
        )}
      </div>

      {/* Breakdown */}
      {breakdown.length > 0 && (
        <div className={styles.breakdown}>
          <div className={styles.barTrack}>
            {breakdown.map((b) => (
              <div
                key={b.name}
                className={styles.barSeg}
                style={{ width: `${(b.total / (todayTotal || 1)) * 100}%`, background: b.color }}
                title={`${b.name}: ${fmtDur(b.total)}`}
              />
            ))}
          </div>
        </div>
      )}

      {/* Recent entries */}
      <div className={styles.entriesSection}>
        <div className={styles.entriesTitle}>Recent Entries</div>
        {entries.length === 0 ? (
          <div className={styles.empty}>No entries yet</div>
        ) : (
          <div className={styles.entryList}>
            {entries.slice(0, 20).map((entry) => (
              <div key={entry.id} className={styles.entryRow}>
                <div className={styles.entryBar} style={{ background: entry.categoryColor }} />
                <div className={styles.entryBody}>
                  <div className={styles.entryTop}>
                    <span className={styles.entryProj} style={{ color: entry.categoryColor }}>
                      {entry.category}
                    </span>
                    {entry.note && <span className={styles.entryNote}>{entry.note}</span>}
                  </div>
                  <div className={styles.entryMeta}>
                    <span className={styles.entryRange}>
                      {fmtRange(entry.startTime, entry.endTime)}
                    </span>
                    <span className={styles.entryDur}>{fmtDur(entry.duration)}</span>
                  </div>
                  {entry.todoTitle && (
                    <div className={styles.entryTodo}>Todo: {entry.todoTitle}</div>
                  )}
                </div>
                <button
                  className={styles.entryDel}
                  onClick={() => deleteEntry.mutate({ id: entry.id })}
                  title="Delete"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
